/**
 * AuthChainVisualization — Shows the auth resolution chain as a horizontal stepper.
 *
 * Displays: JWT → Roles → Scopes → Nav Filter
 * Each step shows pass/fail status and is expandable for details.
 *
 * DRR-315: Unified Auth Trace
 */

import { useMemo } from 'react';
import {
  Group,
  Stack,
  Text,
  Badge,
  ThemeIcon,
  Paper,
  Collapse,
  UnstyledButton,
  Divider,
  Code,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconKey,
  IconUsers,
  IconShield,
  IconNavigation,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

export interface AuthChainStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  status: 'pass' | 'fail' | 'warning' | 'loading';
  summary: string;
  details?: React.ReactNode;
}

interface AuthChainVisualizationProps {
  steps: AuthChainStep[];
}

function ChainStep({ step, isLast }: { step: AuthChainStep; isLast: boolean }) {
  const [opened, { toggle }] = useDisclosure(step.status === 'fail');

  const statusColor = {
    pass: 'green',
    fail: 'red',
    warning: 'orange',
    loading: 'gray',
  }[step.status];

  const StatusIcon = step.status === 'pass' ? IconCheck : step.status === 'fail' ? IconX : IconCheck;

  return (
    <Group gap={0} align="flex-start" style={{ flex: 1 }}>
      <Stack gap="xs" style={{ flex: 1 }}>
        <UnstyledButton onClick={toggle} style={{ width: '100%' }}>
          <Paper p="sm" withBorder radius="md" style={{ borderColor: `var(--mantine-color-${statusColor}-6)` }}>
            <Group justify="space-between">
              <Group gap="xs">
                <ThemeIcon size="md" color={statusColor} variant="light" radius="md">
                  {step.icon}
                </ThemeIcon>
                <Stack gap={0}>
                  <Text size="sm" fw={600}>{step.label}</Text>
                  <Text size="xs" c="dimmed">{step.summary}</Text>
                </Stack>
              </Group>
              <Group gap="xs">
                <ThemeIcon size="sm" color={statusColor} variant="filled" radius="xl">
                  <StatusIcon size={12} />
                </ThemeIcon>
                {step.details && (
                  opened ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
                )}
              </Group>
            </Group>
          </Paper>
        </UnstyledButton>

        {step.details && (
          <Collapse in={opened}>
            <Paper p="sm" withBorder radius="md" ml="md" bg="var(--mantine-color-dark-7)">
              {step.details}
            </Paper>
          </Collapse>
        )}
      </Stack>

      {!isLast && (
        <Group align="center" px="xs" pt="md">
          <Text c="dimmed" size="lg">→</Text>
        </Group>
      )}
    </Group>
  );
}

export function AuthChainVisualization({ steps }: AuthChainVisualizationProps) {
  const passCount = steps.filter(s => s.status === 'pass').length;
  const failCount = steps.filter(s => s.status === 'fail').length;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" fw={600}>Auth Resolution Chain</Text>
        <Group gap="xs">
          <Badge size="sm" color="green" variant="light">{passCount} passed</Badge>
          {failCount > 0 && (
            <Badge size="sm" color="red" variant="light">{failCount} failed</Badge>
          )}
        </Group>
      </Group>

      <Group gap={0} align="flex-start" wrap="nowrap">
        {steps.map((step, i) => (
          <ChainStep key={step.id} step={step} isLast={i === steps.length - 1} />
        ))}
      </Group>
    </Stack>
  );
}

// ── Helper to build steps from auth data ──────────────────────────────────────

export interface AuthTraceData {
  jwtClaims: {
    email?: string;
    groups?: string[];
    ferp_roles?: string[];
    ferp_tenant?: string;
    exp?: number;
  } | null;
  roleIds: string[];
  resolvedScopes: string[];
  blockedItems: {
    path: string;
    label: string;
    reason: string;
    requiredScope?: string;
  }[];
  allowedCount: number;
}

export function buildAuthChainSteps(data: AuthTraceData): AuthChainStep[] {
  const { jwtClaims, roleIds, resolvedScopes, blockedItems, allowedCount } = data;

  const jwtStatus = jwtClaims ? 'pass' : 'fail';
  const rolesStatus = roleIds.length > 0 ? 'pass' : 'fail';
  const scopesStatus = resolvedScopes.length > 0 ? 'pass' : 'fail';
  const navStatus = blockedItems.length === 0 ? 'pass' : blockedItems.length > 5 ? 'fail' : 'warning';

  return [
    {
      id: 'jwt',
      label: 'JWT Claims',
      icon: <IconKey size={14} />,
      status: jwtStatus,
      summary: jwtClaims ? `${jwtClaims.email ?? 'Unknown user'}` : 'No JWT',
      details: jwtClaims ? (
        <Stack gap="xs">
          <Group gap="xs">
            <Text size="xs" c="dimmed" w={80}>Email:</Text>
            <Text size="xs" ff="monospace">{jwtClaims.email ?? 'N/A'}</Text>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed" w={80}>Tenant:</Text>
            <Text size="xs" ff="monospace">{jwtClaims.ferp_tenant ?? 'N/A'}</Text>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed" w={80}>Groups:</Text>
            <Text size="xs" ff="monospace">{jwtClaims.groups?.join(', ') ?? 'None'}</Text>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed" w={80}>Expires:</Text>
            <Text size="xs" ff="monospace">
              {jwtClaims.exp ? new Date(jwtClaims.exp * 1000).toLocaleString() : 'N/A'}
            </Text>
          </Group>
        </Stack>
      ) : undefined,
    },
    {
      id: 'roles',
      label: 'Role IDs',
      icon: <IconUsers size={14} />,
      status: rolesStatus,
      summary: roleIds.length > 0 ? `${roleIds.length} role(s)` : 'No roles',
      details: roleIds.length > 0 ? (
        <Stack gap="xs">
          {roleIds.map(role => (
            <Badge key={role} size="sm" variant="outline" ff="monospace">{role}</Badge>
          ))}
        </Stack>
      ) : undefined,
    },
    {
      id: 'scopes',
      label: 'Resolved Scopes',
      icon: <IconShield size={14} />,
      status: scopesStatus,
      summary: `${resolvedScopes.length} scope(s)`,
      details: resolvedScopes.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">First 20 scopes:</Text>
          <Group gap={4}>
            {resolvedScopes.slice(0, 20).map(scope => (
              <Badge key={scope} size="xs" variant="light" ff="monospace">{scope}</Badge>
            ))}
            {resolvedScopes.length > 20 && (
              <Badge size="xs" color="gray">+{resolvedScopes.length - 20} more</Badge>
            )}
          </Group>
        </Stack>
      ) : undefined,
    },
    {
      id: 'nav',
      label: 'Nav Filtering',
      icon: <IconNavigation size={14} />,
      status: navStatus,
      summary: blockedItems.length === 0
        ? `${allowedCount} items allowed`
        : `${blockedItems.length} blocked, ${allowedCount} allowed`,
      details: blockedItems.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">Blocked items:</Text>
          {blockedItems.slice(0, 10).map(item => (
            <Group key={item.path} gap="xs" wrap="nowrap">
              <Badge size="xs" color="red" variant="light">{item.label}</Badge>
              <Text size="xs" c="dimmed" truncate>{item.reason}</Text>
            </Group>
          ))}
          {blockedItems.length > 10 && (
            <Text size="xs" c="dimmed">+{blockedItems.length - 10} more...</Text>
          )}
        </Stack>
      ) : undefined,
    },
  ];
}
